import { createApi } from '@reduxjs/toolkit/query/react';
import { config } from '../../config/config';
import {
  CreateWorkExperienceReq,
  CreateWorkExperienceRes,
  DeleteWorkExperienceRes,
  GetWorkExperienceRes,
  UpdateWorkExperienceReq,
  UpdateWorkExperienceRes,
} from '../../interfaces/user-work-experience.interface';
import { baseQueryWithInterceptor } from '../utils/baseQueryWithInterceptor';

export interface IWorkExperienceJobTitle {
  title: string;
}

export interface IPaginatedJobTitlesResponse {
  page: number;
  limit: number;
  total: number;
  data: IWorkExperienceJobTitle[];
}

const workExperienceApi = createApi({
  reducerPath: 'workExperienceApi',
  baseQuery: baseQueryWithInterceptor(`${config.API_BASE_URL}/work-experience`),
  tagTypes: ['workExperience'],

  endpoints: (builder) => ({
    getWorkExperiencesByUserId: builder.query<GetWorkExperienceRes[], string>({
      query: (id) => 'userId/' + id,
      providesTags: ['workExperience'],
    }),
    getMyWorkExperiences: builder.query<GetWorkExperienceRes[], void>({
      query: () => `my`,
      providesTags: ['workExperience'],
    }),
    createWorkExperience: builder.mutation<
      CreateWorkExperienceRes,
      CreateWorkExperienceReq
    >({
      query: (data) => ({
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['workExperience'],
    }),
    updateWorkExperienceById: builder.mutation<
      UpdateWorkExperienceRes,
      UpdateWorkExperienceReq
    >({
      query: ({ id, ...restData }) => ({
        url: '/' + id,
        method: 'PUT',
        body: restData,
      }),
      invalidatesTags: ['workExperience'],
    }),
    deleteWorkExperienceById: builder.mutation<DeleteWorkExperienceRes, string>(
      {
        query: (id) => ({
          url: '/' + id,
          method: 'DELETE',
        }),
        invalidatesTags: ['workExperience'],
      }
    ),
    getUniqueJobTitles: builder.query<
      IPaginatedJobTitlesResponse,
      {
        page?: number;
        limit?: number;
        search?: string;
        sort?: string;
      } | void
    >({
      query: (
        args = {
          page: 1,
          limit: 10,
          search: '',
          sort: 'asc',
        }
      ) => {
        const { page = 1, limit = 10, search = '', sort = 'asc' } = args || {};
        return `/unique-job-titles?page=${page}&limit=${limit}&search=${encodeURIComponent(search)}&sort=${sort}`;
      },
      providesTags: ['workExperience'],
    }),
  }),
});

export const {
  useGetWorkExperiencesByUserIdQuery,
  useGetMyWorkExperiencesQuery,
  useCreateWorkExperienceMutation,
  useUpdateWorkExperienceByIdMutation,
  useDeleteWorkExperienceByIdMutation,
  useGetUniqueJobTitlesQuery,
} = workExperienceApi;

export default workExperienceApi;
